import { AlertOctagon, AlertTriangle, Info, ShieldCheck } from 'lucide-react';

import { CapabilityGate } from '../../components/CapabilityGate';

/** Threshold de "carga alta" (design ≈ 18 conversas em atendimento). */
const OVERLOAD_THRESHOLD = 18;

export interface InboxBannersProps {
  p1Count: number;
  conflictCount: number;
  overloadCount: number;
  /**
   * Callback opcional para a ação "Abrir P1". Wire honesto: a InboxPage passa
   * um handler que aplica sort=urgency (sobe os P1 ao topo). Sem rota de
   * supervisor inventada.
   */
  onOpenP1?: () => void;
  /**
   * Callback opcional para "Ver alertas" do ConflictAlert. Só cabeado se
   * trivial; caso contrário o botão fica visível sem handler (honest).
   */
  onViewConflicts?: () => void;
}

/**
 * Banners da Inbox (Brief 2B T1).
 *
 * Exibe alertas contextuais acima da lista:
 *   - P1 (danger): conversas com classificação nível 3+ (urgente)
 *   - ConflictAlert (warning): conversas com conflito adversarial detectado
 *   - Carga alta (info): atendente com >= OVERLOAD_THRESHOLD conversas
 *   - LGPD (Regulated only via CapabilityGate inbox.banner.lgpd)
 */
export function InboxBanners({
  p1Count,
  conflictCount,
  overloadCount,
  onOpenP1,
  onViewConflicts,
}: InboxBannersProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2 px-6 py-3" aria-live="polite">
      {p1Count > 0 && (
        <div className="banner-2b banner-2b--danger" role="alert">
          <AlertOctagon className="banner-2b__icon" aria-hidden />
          <div className="banner-2b__msg">
            <strong>
              {p1Count} conversa{p1Count > 1 ? 's' : ''} em P1
            </strong>{' '}
            — abrir agora antes de novas atribuições.
          </div>
          <button type="button" className="btn btn--destructive-solid btn--sm" onClick={onOpenP1}>
            Abrir P1
          </button>
        </div>
      )}

      {conflictCount > 0 && (
        <div className="banner-2b banner-2b--warning" role="alert">
          <AlertTriangle className="banner-2b__icon" aria-hidden />
          <div className="banner-2b__msg">
            <strong>
              {conflictCount} conversa{conflictCount > 1 ? 's' : ''} com ConflictAlert
            </strong>{' '}
            severity high+ — revisar imediatamente.
          </div>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onViewConflicts}>
            Ver alertas
          </button>
        </div>
      )}

      {overloadCount >= OVERLOAD_THRESHOLD && (
        <div className="banner-2b banner-2b--info" role="alert">
          <Info className="banner-2b__icon" aria-hidden />
          <div className="banner-2b__msg">
            Você tem <strong>{overloadCount} conversas em atendimento</strong>. Considere puxar
            pendências antes de novas conversas.
          </div>
        </div>
      )}

      <CapabilityGate capability="inbox.banner.lgpd">
        <div className="banner-2b banner-2b--lgpd" role="alert">
          <ShieldCheck className="banner-2b__icon" aria-hidden />
          <div className="banner-2b__msg">
            <strong>Operação Regulada</strong> — todas as visualizações e filtros são auditados em
            audit log persistente.
          </div>
        </div>
      </CapabilityGate>
    </div>
  );
}
